import { AUTH as type } from '@/store/types';
import Affiliates from '@/services/Api/Affiliates';
import Impersonation from '@/services/Api/Impersonation';
import { getKeycloakLogoutRedirectUri, getKeycloakRedirectUri } from '@/services/keycloakRedirect';
import fetchMemberOrganizations from '@/services/memberOrganizations';
import api from '@/services/Api';
import store from '@/store';

let authInterceptorRegistered = false;

const IMPERSONATION_REFRESH_MS = 120000;
let impersonationRefreshTimer = null;
let impersonationRefreshInflight = null;
let impersonationRefreshVisibilityBound = false;

function onImpersonationVisibility() {
  if (document.visibilityState === 'visible') {
    store.dispatch('auth/refreshImpersonationTokens').catch(() => {});
  }
}

function stopImpersonationRefresh() {
  if (impersonationRefreshTimer) {
    clearInterval(impersonationRefreshTimer);
    impersonationRefreshTimer = null;
  }
  if (typeof document !== 'undefined' && impersonationRefreshVisibilityBound) {
    document.removeEventListener('visibilitychange', onImpersonationVisibility);
    impersonationRefreshVisibilityBound = false;
  }
}

function startImpersonationRefresh() {
  stopImpersonationRefresh();
  impersonationRefreshTimer = setInterval(() => {
    store.dispatch('auth/refreshImpersonationTokens').catch(() => {});
  }, IMPERSONATION_REFRESH_MS);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onImpersonationVisibility);
    impersonationRefreshVisibilityBound = true;
  }
}

function registerAuthInterceptor() {
  if (authInterceptorRegistered) {
    return;
  }

  authInterceptorRegistered = true;
  api.interceptors.request.use((config) => {
    const token = store.getters['auth/tokens']?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    return config;
  });
}

const state = () => ({
  user: null,
  keycloakProfile: null,
  isAuthenticating: false,
  isAuthenticated: false,
  redirectTo: null,
  keycloak: null,
  tokens: null,
  impersonation: null,
});

const mutations = {
  [type.AUTHENTICATING.SET](state, value) {
    state.isAuthenticating = value;
  },

  [type.AUTHENTICATED.SET](state, value) {
    state.isAuthenticated = value;
  },

  [type.REDIRECT.SET](state, routeName) {
    state.redirectTo = routeName;
  },

  [type.KEYCLOAK.SET](state, keycloak) {
    state.keycloak = keycloak;
  },

  [type.KEYCLOAK.TOKENS.SET](state, tokens) {
    state.tokens = tokens;
  },

  [type.KEYCLOAK.PROFILE.SET](state, keycloakProfile) {
    state.keycloakProfile = keycloakProfile;
  },

  [type.USER.SET](state, user) {
    state.user = user;
  },

  [type.IMPERSONATION.SET](state, payload) {
    state.impersonation = payload;
  },
};

const actions = {
  async setKeycloakProfile({ commit }) {
    const { $keycloak } = this._vm;
    const profile = await $keycloak.loadUserProfile();
    commit(type.KEYCLOAK.PROFILE.SET, profile);
  },

  async setUser({ commit }, value = null) {
    const user = value || await Affiliates.me();
    commit(type.USER.SET, user.data.data);
  },

  async setAuthenticated({ commit }, authenticated) {
    commit(type.AUTHENTICATED.SET, authenticated);
  },

  // will keep an eye on $keycloak.authenticated and make sure
  // our store is kept up-to-date with the same value
  async monitorAuthenticated({ dispatch }) {
    const monitor = () => {
      const { $keycloak } = this._vm;
      const isAuthenticated = store.getters['auth/isAuthenticated'];
      if ($keycloak.authenticated !== isAuthenticated && isAuthenticated) {
        dispatch('auth/logout');
      }
    };

    monitor();
    setInterval(monitor, 1000 * 10);
  },

  async resetUser({ commit }) {
    stopImpersonationRefresh();
    commit(type.USER.SET, null);
    commit(type.KEYCLOAK.PROFILE.SET, null);
    commit(type.AUTHENTICATED.SET, false);
    commit(type.IMPERSONATION.SET, null);
  },

  login({ commit }) {
    commit(type.AUTHENTICATING.SET, true);
    this._vm.$keycloak.login({
      redirectUri: getKeycloakRedirectUri(),
    });
  },

  register() {
    this._vm.$keycloak.register({
      redirectUri: getKeycloakRedirectUri(),
    });
  },

  async logout({ commit }) {
    commit(type.AUTHENTICATING.SET, false);
    await this._vm.$keycloak.logout({
      redirectUri: getKeycloakLogoutRedirectUri(),
    });
  },

  async endImpersonation({ dispatch, commit }) {
    stopImpersonationRefresh();
    commit(type.AUTHENTICATING.SET, false);
    await dispatch('resetUser');
    await dispatch('setKeycloakTokens', {});
    commit(type.IMPERSONATION.SET, null);
    await fetchMemberOrganizations();
  },

  async redirectAfterAuth({ commit }, routeName) {
    commit(type.REDIRECT.SET, routeName);
  },

  async isAuthenticating({ commit }, isAuthenticating) {
    commit(type.AUTHENTICATING.SET, isAuthenticating);
  },

  async setKeycloakTokens({ commit }, tokens) {
    registerAuthInterceptor();
    commit(type.KEYCLOAK.TOKENS.SET, tokens);
  },

  async refreshImpersonationTokens({ state, dispatch, getters }) {
    if (!getters.isImpersonating) {
      return undefined;
    }

    const refreshToken = state.tokens?.refreshToken;
    if (!refreshToken) {
      return undefined;
    }

    if (impersonationRefreshInflight) {
      return impersonationRefreshInflight;
    }

    impersonationRefreshInflight = Impersonation.refresh(refreshToken)
      .then(async ({ data: response }) => {
        const payload = response?.data ?? {};
        if (!payload.token) {
          throw new Error('Impersonation refresh returned no token');
        }
        await dispatch('setKeycloakTokens', {
          token: payload.token,
          refreshToken: payload.refreshToken || refreshToken,
        });
      })
      .catch(async (error) => {
        await dispatch('endImpersonation');
        throw error;
      })
      .finally(() => {
        impersonationRefreshInflight = null;
      });

    return impersonationRefreshInflight;
  },

  async startImpersonation({ commit, dispatch }, payload) {
    await dispatch('setKeycloakTokens', {
      token: payload.token,
      refreshToken: payload.refreshToken,
    });
    await dispatch('setUser');
    await dispatch('setAuthenticated', true);
    commit(type.IMPERSONATION.SET, payload.impersonation ?? null);
    await fetchMemberOrganizations();

    if (payload.refreshToken) {
      startImpersonationRefresh();
    } else {
      console.warn('Impersonation started without a refresh token; session will expire with the access token.');
    }
  },

  async consumeImpersonationTicket({ dispatch }, ticket) {
    const { data: response } = await Impersonation.consume(ticket);
    const payload = response?.data ?? {};

    await dispatch('startImpersonation', payload);
  },
};

const getters = {
  user: (state) => state.user,
  isAuthenticating: (state) => state.isAuthenticating,
  isAuthenticated: (state) => state.isAuthenticated,
  tokens: (state) => state.tokens,
  keycloakProfile: (state) => state.keycloakProfile,
  impersonation: (state) => state.impersonation,
  isImpersonating: (state) => !!state.impersonation?.isImpersonating,
  redirectRoute: (state) => (state.redirectTo ? state.redirectTo : null),
  isRegistrationComplete: (state) => state.user?.isRegistered,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
